import {
  AccountCircle,
  Assignment,
  AutoAwesome,
  CheckCircle,
  Error,
  School,
} from '@mui/icons-material';
import { Box, Card, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';
import i18n from 'i18next';
import React from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import {
  ApplicationResourceTypeDto,
  NotificationPresentationDto,
  NotificationTypeDto,
  SurveyExecutionStatusDto,
  SurveyStatusDto,
} from '@/api/generated';
import { useApiClient } from '@/api/useApiClient.util';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { parseServerLocalDateTime } from '@/utils/formatDate.util.ts';
import { getDateFnsLocale } from '@/utils/getDateFnsLocale.util.ts';
import { useSetNotificationAsSeen } from '@/utils/notifications/useSetNotificationAsSeen.util';
import { useSetNotificationAsUnseen } from '@/utils/notifications/useSetNotificationAsUnseen.util';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { MY_PROFILE_ROUTES } from '@/views/myProfile/constants/myProfile.constants';

interface Props {
  notification: NotificationPresentationDto;
  userId: string;
  variant?: 'compact' | 'large' | 'topBar';
}

export const NotificationPresentation: React.FC<Props> = ({
  notification,
  userId,
  variant = 'compact',
}) => {
  const theme = useTheme();
  const { applicationResource, content, createdAt, notificationId, recipientSeenAt, title, type } =
    notification;

  const { t: notificationLabel } = useTranslationWithPrefix('dictionaries.notifications');
  const { t } = useTranslationWithPrefix('components.notifications');
  const { t: tNotifications } = useTranslationWithPrefix('views.notifications');

  const { setNotificationAsSeen } = useSetNotificationAsSeen();
  const { setNotificationAsUnseen } = useSetNotificationAsUnseen();
  const { surveyParticipationApi } = useApiClient();
  const navigate = useNavigate();

  const isRead = Boolean(recipientSeenAt);

  const getNotificationIcon = () => {
    switch (type) {
      case NotificationTypeDto.DevelopmentPlanInitialized:
      case NotificationTypeDto.DevelopmentPlanToSupervisorAcceptanceRequested:
        return <AutoAwesome sx={{ color: theme.colors.accentInk, fontSize: 18 }} />;
      case NotificationTypeDto.DevelopmentPlanSupervisorAccepted:
        return <CheckCircle sx={{ color: theme.colors.green, fontSize: 18 }} />;
      case NotificationTypeDto.DevelopmentPlanSupervisorRejected:
        return <Error sx={{ color: theme.colors.red, fontSize: 18 }} />;
      case NotificationTypeDto.DevelopmentPlanRequired:
        return <Assignment sx={{ color: theme.colors.accentInk, fontSize: 18 }} />;
      case NotificationTypeDto.DevelopmentStepOverdue:
        return <Error sx={{ color: theme.colors.red, fontSize: 18 }} />;
      case NotificationTypeDto.SurveyAssigned:
        return <Assignment sx={{ color: theme.colors.accentInk, fontSize: 18 }} />;
      default:
        return <AutoAwesome sx={{ color: theme.colors.accentInk, fontSize: 18 }} />;
    }
  };

  const getTypeColor = () => {
    switch (type) {
      case NotificationTypeDto.DevelopmentPlanSupervisorAccepted:
        return 'linear-gradient(to bottom, #4EC35A, #48A550)';
      case NotificationTypeDto.DevelopmentPlanSupervisorRejected:
        return 'linear-gradient(to bottom, #BF4751, #A1434A)';
      case NotificationTypeDto.DevelopmentPlanToSupervisorAcceptanceRequested:
        return 'linear-gradient(to bottom, #CA7E4A, #A96E46)';
      case NotificationTypeDto.DevelopmentPlanRequired:
        return 'linear-gradient(to bottom, #CA7E4A, #A96E46)';
      case NotificationTypeDto.DevelopmentStepOverdue:
        return 'linear-gradient(to bottom, #BF4751, #A1434A)';
      case NotificationTypeDto.SurveyAssigned:
        return 'linear-gradient(to bottom, #2196F3, #1976D2)';
      default:
        return '#232323';
    }
  };

  const handleToggleRead = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    const reqBody = {
      markNotificationAsSeenRequestDto: {
        userId,
      },
      notificationId,
    };
    if (isRead) {
      await setNotificationAsUnseen(reqBody);
    } else {
      await setNotificationAsSeen(reqBody);
    }
  };

  const canChangeReadStatus = notification.recipientId === userId;
  const isLarge = variant === 'large';
  const isTopBar = variant === 'topBar';
  const isSurveyNotification = type === NotificationTypeDto.SurveyAssigned;
  const isClickable =
    applicationResource.resourceType === ApplicationResourceTypeDto.EmployeeProfile &&
    !isLarge &&
    !isSurveyNotification;

  const handleOnClick = async () => {
    if (applicationResource.resourceType !== ApplicationResourceTypeDto.EmployeeProfile) return;

    navigate(
      generatePath(RouteEnum.EMPLOYEE_PROFILE_NEW, { '*': '', id: applicationResource.resourceId }),
    );

    if (!isRead && canChangeReadStatus) {
      await setNotificationAsSeen({
        markNotificationAsSeenRequestDto: { userId },
        notificationId,
      });
    }
  };

  const markNotificationAsSeenIfNeeded = async () => {
    if (!isRead && canChangeReadStatus) {
      await setNotificationAsSeen({
        markNotificationAsSeenRequestDto: { userId },
        notificationId,
      });
    }
  };

  const goToSurveyTarget = async () => {
    const surveyId = parseInt(applicationResource.resourceId, 10);

    try {
      const statusResponse = await surveyParticipationApi.getSurveyStatus(surveyId);
      const { deadline, status, surveyStatus } = statusResponse.data;
      const isDeadlineExpired = deadline ? new Date(deadline) < new Date() : false;
      const isSurveyUnavailable =
        isDeadlineExpired ||
        surveyStatus === SurveyStatusDto.Cancelled ||
        surveyStatus === SurveyStatusDto.Completed;

      if (isSurveyUnavailable || status === SurveyExecutionStatusDto.Completed) {
        navigate(RouteEnum.CERTIFICATION);
      } else {
        navigate(generatePath(RouteEnum.SURVEY_FILL, { surveyId: applicationResource.resourceId }));
      }

      await markNotificationAsSeenIfNeeded();
    } catch {
      navigate(RouteEnum.CERTIFICATION);
      await markNotificationAsSeenIfNeeded();
    }
  };

  const handleProfileClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isLarge) {
      navigate(MY_PROFILE_ROUTES.basicData);
      await markNotificationAsSeenIfNeeded();
      return;
    }

    await handleOnClick();
  };

  const handleGoToSurvey = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    await goToSurveyTarget();
  };

  const handleGoToMySurveys = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    navigate(RouteEnum.CERTIFICATION);
    if (!isRead && canChangeReadStatus) {
      await setNotificationAsSeen({
        markNotificationAsSeenRequestDto: { userId },
        notificationId,
      });
    }
  };

  const handleNotificationClick = async () => {
    await handleOnClick();
  };

  const handleTopBarClick = async () => {
    if (isSurveyNotification) {
      await goToSurveyTarget();
      return;
    }

    await handleOnClick();
  };

  const parseStartFromContent = (text: string): Date | null => {
    const match = text.match(/Start:\s*(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/);
    if (!match) return null;
    const [, dd, mm, yyyy, hh, min] = match;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min));
  };

  const referenceDate =
    parseStartFromContent(content) ?? parseServerLocalDateTime(createdAt) ?? new Date(createdAt);
  const timeAgo = formatDistanceToNow(referenceDate, {
    addSuffix: true,
    locale: getDateFnsLocale(i18n.language),
  });

  const getTranslatedText = (text: string) => {
    if (text.startsWith('notification.')) {
      const key = text.replace('notification.', '');
      const translated = tNotifications(key);
      return translated !== key ? translated : text;
    }
    return text;
  };

  const displayTitle = getTranslatedText(title);
  const displayContent = getTranslatedText(content);

  if (isLarge) {
    return (
      <Box
        onClick={isClickable ? handleNotificationClick : undefined}
        sx={{
          '&:hover': {
            '& .notification-row-actions': {
              opacity: 1,
            },
            backgroundColor: theme.colors.bgHover,
          },
          '&:not(:last-of-type)': {
            borderBottom: `1px solid ${theme.colors.border}`,
          },
          backgroundColor: isRead ? theme.colors.bgCard : theme.colors.bgCard2,
          boxShadow: !isRead ? `inset 3px 0 0 ${theme.colors.accent}` : 'none',
          cursor: isClickable ? 'pointer' : 'default',
          px: { md: 3, xs: 2 },
          py: 2.5,
          transition:
            'background-color 180ms cubic-bezier(0.2,0,0,1), box-shadow 180ms cubic-bezier(0.2,0,0,1)',
          width: '100%',
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box sx={{ mt: 0.2 }}>{getNotificationIcon()}</Box>

          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack
              direction="row"
              sx={{
                alignItems: 'flex-start',
                gap: 1.5,
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  color: isRead ? theme.colors.textSecondary : theme.colors.textPrimary,
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.3,
                  textWrap: 'pretty',
                }}
              >
                {displayTitle}
              </Typography>
            </Stack>

            <Typography
              sx={{
                color: theme.colors.textSecondary,
                fontSize: 13,
                lineHeight: 1.45,
                textWrap: 'pretty',
              }}
            >
              {displayContent}
            </Typography>

            <Typography
              sx={{
                color: theme.colors.textMuted,
                fontSize: 11,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.3,
                pt: 1,
              }}
            >
              {timeAgo}
            </Typography>
          </Stack>

          <Stack
            className="notification-row-actions"
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: 'center',
              flexShrink: 0,
              opacity: { md: 0, xs: 1 },
              transition: 'opacity 180ms cubic-bezier(0.2,0,0,1)',
            }}
          >
            {!isSurveyNotification && (
              <Tooltip title={t('viewProfile')}>
                <IconButton
                  aria-label={t('viewProfile')}
                  onClick={handleProfileClick}
                  size="small"
                  sx={{
                    '&:active': { scale: '0.94' },
                    '&:hover': {
                      background: theme.colors.bgHover,
                      color: theme.colors.textPrimary,
                    },
                    borderRadius: 2,
                    color: theme.colors.textSecondary,
                    transition:
                      'background-color 180ms cubic-bezier(0.2,0,0,1), color 180ms, scale 120ms',
                  }}
                >
                  <AccountCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isSurveyNotification && (
              <>
                <Tooltip title={t('goToSurvey')}>
                  <IconButton
                    aria-label={t('goToSurvey')}
                    onClick={handleGoToSurvey}
                    size="small"
                    sx={{
                      '&:active': { scale: '0.94' },
                      '&:hover': {
                        background: theme.colors.accentBg,
                        color: theme.colors.accentInk,
                      },
                      borderRadius: 2,
                      color: theme.colors.textSecondary,
                      transition:
                        'background-color 180ms cubic-bezier(0.2,0,0,1), color 180ms, scale 120ms',
                    }}
                  >
                    <Assignment fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('goToMySurveys')}>
                  <IconButton
                    aria-label={t('goToMySurveys')}
                    onClick={handleGoToMySurveys}
                    size="small"
                    sx={{
                      '&:active': { scale: '0.94' },
                      '&:hover': {
                        background: theme.colors.bgHover,
                        color: theme.colors.textPrimary,
                      },
                      borderRadius: 2,
                      color: theme.colors.textSecondary,
                      transition:
                        'background-color 180ms cubic-bezier(0.2,0,0,1), color 180ms, scale 120ms',
                    }}
                  >
                    <School fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {canChangeReadStatus && (
              <Tooltip title={isRead ? t('markAsUnread') : t('markAsRead')}>
                <IconButton
                  aria-label={isRead ? t('markAsUnread') : t('markAsRead')}
                  onClick={handleToggleRead}
                  size="small"
                  sx={{
                    '&:active': { scale: '0.94' },
                    '&:hover': {
                      background: isRead ? theme.colors.bgHover : theme.colors.accentBg,
                      color: isRead ? theme.colors.textPrimary : theme.colors.accentInk,
                    },
                    borderRadius: 2,
                    color: isRead ? theme.colors.textMuted : theme.colors.accentInk,
                    transition:
                      'background-color 180ms cubic-bezier(0.2,0,0,1), color 180ms, scale 120ms',
                  }}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Box>
    );
  }

  if (isTopBar) {
    return (
      <Box
        onClick={handleTopBarClick}
        sx={{
          '&:hover': {
            backgroundColor: theme.colors.bgHover,
          },
          alignItems: 'flex-start',
          backgroundColor: 'transparent',
          borderBottom: `1px solid ${theme.colors.border}`,
          cursor: isClickable || isSurveyNotification ? 'pointer' : 'default',
          display: 'flex',
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          transition: 'background-color 0.2s cubic-bezier(0.4,0,0.2,1)',
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            borderRadius: '50%',
            display: 'flex',
            flexShrink: 0,
            height: 24,
            justifyContent: 'center',
            width: 24,
          }}
        >
          {getNotificationIcon()}
        </Box>
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: theme.colors.textPrimary,
              fontSize: '13px',
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            {displayTitle}
          </Typography>
          <Typography
            sx={{
              color: theme.colors.textSecondary,
              fontSize: '12px',
              lineHeight: 1.4,
              whiteSpace: 'normal',
            }}
          >
            {displayContent}
          </Typography>
          <Typography sx={{ color: theme.colors.textMuted, fontSize: '12px', lineHeight: 1.2 }}>
            {timeAgo}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Card
      onClick={isClickable ? handleNotificationClick : undefined}
      sx={{
        '&:hover': {
          boxShadow: 1,
          transform: 'translateY(-1px)',
        },
        backgroundColor: isRead ? theme.colors.bg : theme.colors.bgAccent,
        border: `1px solid ${theme.colors.stroke}`,
        borderLeft: isRead
          ? `1px solid ${theme.colors.stroke}`
          : `4px solid ${theme.colors.accent}`,
        borderRadius: 5,
        cursor: isClickable ? 'pointer' : 'default',
        px: 2.5,
        py: 2,
        transition:
          'background-color 180ms cubic-bezier(0.2,0,0,1), box-shadow 180ms cubic-bezier(0.2,0,0,1), transform 180ms cubic-bezier(0.2,0,0,1)',
        width: 400,
      }}
    >
      <Stack
        sx={{
          gap: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          {getNotificationIcon()}
          <Chip
            label={notificationLabel(type)}
            size="small"
            sx={{
              background: getTypeColor(),
              color: 'white',
            }}
          />
        </Stack>

        <Typography
          sx={{
            color: isRead ? 'text.secondary' : 'text.primary',
          }}
          variant="newF16Medium"
        >
          {displayTitle}
        </Typography>

        <Typography
          sx={{
            color: 'text.secondary',
            textWrap: 'balance',
          }}
          variant="newBodyText"
        >
          {displayContent}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              color: 'text.secondary',
            }}
            variant="caption"
          >
            {timeAgo}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
            }}
          >
            {canChangeReadStatus && (
              <Tooltip title={isRead ? t('markAsUnread') : t('markAsRead')}>
                <IconButton
                  onClick={handleToggleRead}
                  size="small"
                  sx={{
                    '&:hover': { opacity: 1 },
                    opacity: 0.7,
                  }}
                >
                  <CheckCircle color={isRead ? 'disabled' : 'primary'} fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
